import React, { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import DashboardLayout from "../layouts/DashboardLayout";
import SettingsLayout from "../layouts/SettingsLayout";
import SetupLayout from "../layouts/SetupLayout";
import WebLayout from "../layouts/WebLayout";
import Organization from "../views/settings/Organization";
import Designations from "../views/settings/Designations";
import OrgLevels from "../views/settings/OrgLevels";
import Users from "../views/settings/Users";
import UserProfile from "../views/settings/UserProfile";
import SetupAccount from "../views/setup/SetupAccount";
import ForgotPassword from "../views/web/ForgotPassword";
import Login from "../views/web/Login";
import ResetPassword from "../views/web/ResetPassword";
import ChangePassword from "../views/settings/ChangePassword";
import Customization from "../views/settings/Customization";
import PlanTemplates from "../views/settings/PlanTemplates";
import PerformanceCycle from "../views/dashboard/PerformanceCycle";
import SingleUnit from "../views/settings/SingleUnit";
import SingleUser from "../views/settings/SingleUser";
import General from "../views/settings/General";
import PerformanceCycles from "../views/settings/PerfCycles";
import Reports from "../views/dashboard/Reports";
import UploadTasksPage from "../views/dashboard/UploadTasksPage";
import SinglePlan from "../views/dashboard/SinglePlan";
import Approvals from "../views/settings/Approvals";
import Home from "../views/dashboard/Home";
import Unit from "../views/dashboard/Unit";
import TopPlan from "../views/dashboard/TopPlan";
import Units from "../views/dashboard/Units";
import TopPlans from "../views/dashboard/TopPlans";
import Individual from "../views/dashboard/PositionPerformance";
import ProfileSetupLayout from "../layouts/ProfileSetupLayout";
import ProfileSetup from "../views/profileSetup/ProfileSetup";
import { useDispatch, useSelector } from "react-redux";
import { GetMeApi } from "../api/users/GetMeApi";
import { userDetails } from "../features/UserSlice";
import { login, logout } from "../features/AuthSlice";
import { firstLogin } from "../features/FirstLoginSlice";
import MainLoader from "../components/sub/MainLoader";
import AppRoute from "./AppRoute";
import OpenRoute from "./OpenRoute";
import ProtectedRoute from "./ProtectedRoute";
import SetupProfileRoute from "./SetupProfileRoute";
import SetupAccountRoute from "./SetupAccountRoute";
import { setupAccount } from "../features/SetupAccountSlice";
import SetupGeneral from "../views/setup/SetupGeneral";
import PlanGroups from "../views/settings/PlanGroups";
import Scales from "../views/settings/Scales";
import PlanTemplate from "../views/settings/PlanTemplate";
import PlanAttributes from "../views/settings/PlanAttributes";
import DropdownAttributes from "../views/settings/DropdownAttributes";
import Positions from "../views/settings/Positions";
import { positionDetails } from "../features/PositionSlice";
import PositionsPerformance from "../views/dashboard/PositionsPerformance";
import PositionPerformance from "../views/dashboard/PositionPerformance";
import PositionNotFound from "../views/web/PositionNotFound";
import PositionRoute from "./PositionRoute";
import MyPlans from "../views/dashboard/MyPlans";
import Timeline from "../views/dashboard/Timeline";
import Timesheets from "../views/dashboard/Timesheets";
import TargetApprovals from "../views/settings/TargetApprovals";
import MetabaseDashboard from "../views/dashboard/MetabaseDashboard";
import PlanApprovals from "../views/settings/PlanApprovals";
import ChatbotSettings from "../views/settings/ChatbotSettings";
import ZohoIntegration from "../views/settings/ZohoIntegration";
import OdooIntegration from "../views/settings/OdooIntegration";
import PositionFeatures from "../views/settings/PositionFeatures";
import PerformanceCycleLock from "../views/settings/PerformanceCycleLock";
import Prediction from "../views/dashboard/Prediction";
import FeatureGuard from "../components/guards/FeatureGuard";
import PeopleHR from "../views/dashboard/PeopleHR";
import EhrmConfig from "../components/settings/EhrmConfig";
import ClickUpConfig from "../components/settings/ClickUpConfig";
import WhatsAppConfig from "../components/settings/WhatsAppConfig";
import SsoConfig from "../components/settings/SsoConfig";
import GemChatPage from "../views/chat/GemChatPage"; 
import LoginAudit from "../views/settings/LoginAudit";
import PingLoginAuditApi from "../api/login-audit/PingLoginAuditApi";
import SsoLanding from "../views/web/SsoLanding";
import LinkAccount from "../views/web/LinkAccount";
import { useGlobalSettings } from "../context/GlobalSettingsContext";

// Redirects to the org-configured landing page
function LandingRedirect() {
  const { defaultLandingPage } = useGlobalSettings();
  return <Navigate replace to={defaultLandingPage || "/my-plans"} />;
}

function Main() {
  const authState = useSelector((state) => state.authState.value);
  const userState = useSelector((state) => state.userState.value);
  const dispatch = useDispatch();

  useEffect(() => {
    getUserDetails(userState.id);
  }, [authState]);

  async function getUserDetails() {
    const apiResponse = await GetMeApi();
    if (apiResponse.statusCode) {
      localStorage.setItem("authToken", "");
      localStorage.setItem("authUser", "");
      dispatch(logout());
    } else if (apiResponse.success) {
      dispatch(login());
      dispatch(userDetails(apiResponse.data));

      // Record first visit of the day (once per day, purely local check)
      const today = new Date().toISOString().split('T')[0];
      const lastAuditDate = localStorage.getItem('lastAuditDate');
      if (lastAuditDate !== today) {
        localStorage.setItem('lastAuditDate', today);
        PingLoginAuditApi(); // fire-and-forget
      }
      const lastPosition = localStorage.getItem("stPosition");
      if (lastPosition) {
        const position = apiResponse.data?.positions.find(
          (pos) => pos.id === lastPosition
        );
        if (position) {
          dispatch(positionDetails(position));
        } else {
          dispatch(positionDetails(apiResponse.data?.positions[0]));
          localStorage.setItem(
            "stPosition",
            apiResponse.data?.positions[0]?.id
          );
        }
      } else {
        dispatch(positionDetails(apiResponse.data?.positions[0]));
        localStorage.setItem("stPosition", apiResponse.data?.positions[0]?.id);
      }

      /*If user login for the first time */
      if (apiResponse.data?.firstLogin) {
        dispatch(firstLogin(true));
      }
      if (
        !apiResponse.data?.firstLogin &&
        apiResponse.data?.positions.length === 0
      ) {
        // dispatch(firstLogin(true));
      }
      /*If account setup didn't complete */
      if (!apiResponse.data?.setupAccount) {
        dispatch(setupAccount(false));
      }
    } else {
      localStorage.setItem("authToken", "");
      localStorage.setItem("authUser", "");
      dispatch(logout());
    }
  }

  return authState ? (
    <Routes>
      <Route element={<OpenRoute />}>
        <Route element={<WebLayout />}>
          <Route path="login" element={<Login />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password/:token" element={<ResetPassword />} />
          <Route path="position-not-found" element={<PositionNotFound />} />
          {/* SSO entry points — no auth required */}
          <Route path="sso" element={<SsoLanding />} />
          <Route path="link" element={<LinkAccount />} />
          <Route path="*" element={<Navigate replace to="/login" />} />
        </Route>
      </Route>
      <Route element={<ProtectedRoute />}>
        <Route element={<SetupProfileRoute />}>
          <Route element={<ProfileSetupLayout />}>
            <Route path="setup-profile" element={<ProfileSetup />} />
          </Route>
        </Route>
        <Route element={<SetupAccountRoute />}>
          <Route path="setup-account/*" element={<SetupLayout />}>
            <Route path="overview" element={<SetupAccount />} />
            <Route path="user-profile" element={<UserProfile />} />
            <Route path="change-password" element={<ChangePassword />} />
            <Route path="general/*" element={<SetupGeneral />}>
              <Route path="org-levels" element={<OrgLevels />} />
              <Route path="plan-templates" element={<PlanTemplates />} />
              <Route path="plan-templates/:id" element={<PlanTemplate />} />
              <Route path="plan-groups" element={<PlanGroups />} />
              <Route path="designations" element={<Designations />} />
              <Route path="plan-attributes" element={<PlanAttributes />} />
              <Route path="scales" element={<Scales />} />
              <Route path="chatbot" element={<ChatbotSettings />} />
              <Route path="zoho-crm" element={<ZohoIntegration />} />
              <Route path="odoo-crm" element={<OdooIntegration />} />
              <Route
                path="*"
                element={
                  <Navigate replace to="/setup-account/general/org-levels" />
                }
              />
            </Route>
            <Route path="organization" element={<Organization />} />
            <Route path="unit/:id" element={<SingleUnit />} />
            <Route path="positions" element={<Positions />} />
            <Route path="users" element={<Users />} />
            <Route path="perf-cycles" element={<PerformanceCycles />} />
            <Route path="approvals" element={<Approvals />} />
            <Route path="target-approvals" element={<TargetApprovals />} />
            <Route path="plan-approvals" element={<PlanApprovals />} />
            <Route path="chatbot" element={<ChatbotSettings />} />
            <Route
              path="*"
              element={<Navigate replace to="/setup-account/overview" />}
            />
          </Route>
        </Route>
        <Route element={<PositionRoute />}>
          <Route path="/*" element={<WebLayout />}>
            <Route path="not-found" element={<PositionNotFound />} />
            <Route path="*" element={<Navigate to="/not-found" />} />
          </Route>
        </Route>
        <Route element={<AppRoute />}>
          <Route path="/*" element={<DashboardLayout />}>
            <Route path="home" element={<Home />} />
            <Route path="people-hr" element={<PeopleHR />} />
            <Route path="metabase" element={
              <FeatureGuard feature="dashboard">
                <MetabaseDashboard />
              </FeatureGuard>
            } />
            <Route path="prediction" element={
              <FeatureGuard feature="prediction">
                <Prediction />
              </FeatureGuard>
            } />
            <Route path="gem" element={
              <FeatureGuard feature="gem">
                <GemChatPage />
              </FeatureGuard>
            } />
            <Route path="my-plans" element={<MyPlans />} />
            <Route path="timeline" element={
              <FeatureGuard feature="timeline">
                <Timeline />
              </FeatureGuard>
            } />
            <Route path="timesheets" element={
              <FeatureGuard feature="timesheet">
                <Timesheets />
              </FeatureGuard>
            } />
            <Route
              path="my-plans/:id"
              element={<TopPlan planType="my-plans" />}
            />
            <Route path="units" element={
              <FeatureGuard feature="units">
                <Units />
              </FeatureGuard>
            } />
            <Route path="units/:id" element={
              <FeatureGuard feature="units">
                <Unit />
              </FeatureGuard>
            } />
            <Route
              path="top-plans"
              element={<TopPlans planType="top-plans" />}
            />
            <Route path="top-plans/:id" element={<TopPlan />} />
            <Route
              path="positions-performance"
              element={
                <FeatureGuard feature="positions">
                  <PositionsPerformance />
                </FeatureGuard>
              }
            />
            <Route
              path="positions-performance/:id"
              element={
                <FeatureGuard feature="positions">
                  <PositionPerformance />
                </FeatureGuard>
              }
            />
            <Route path="organization" element={<Organization />} />
            <Route path="unit" element={<SingleUnit />} />
            <Route path="users" element={<Users />} />
            <Route path="user" element={<SingleUser />} />
            <Route path="performance-cycles" element={<PerformanceCycles />} />
            <Route path="performance-cycle" element={<PerformanceCycle />} />
            <Route path="plan" element={<SinglePlan />} />
            <Route path="reports" element={<Reports />} />
            <Route path="upload-tasks" element={<UploadTasksPage />} />

            <Route path="*" element={<LandingRedirect />} />
          </Route>
          <Route path="settings/*" element={<SettingsLayout />}>
            <Route path="user-profile" element={<UserProfile />} />
            <Route path="change-password" element={<ChangePassword />} />
            <Route path="customization" element={<Customization />} />
            <Route path="general/*" element={<General />}>
              <Route path="org-levels" element={<OrgLevels />} />
              <Route path="plan-templates" element={<PlanTemplates />} />
              <Route path="plan-templates/:id" element={<PlanTemplate />} />
              <Route path="plan-groups" element={<PlanGroups />} />
              <Route path="designations" element={<Designations />} />
              <Route path="plan-attributes" element={<PlanAttributes />} />
              <Route path="dropdown-attributes" element={<DropdownAttributes />} />
              <Route path="scales" element={<Scales />} />
              <Route path="position-features" element={<PositionFeatures />} />
              <Route path="cycle-lock" element={<PerformanceCycleLock />} />
              <Route path="chatbot" element={<ChatbotSettings />} />
              <Route path="zoho-crm" element={<ZohoIntegration />} />
              <Route path="odoo-crm" element={<OdooIntegration />} />
              <Route path="ehrm-config" element={<EhrmConfig />} />
              <Route path="clickup-config" element={<ClickUpConfig />} />
              <Route path="whatsapp-config" element={<WhatsAppConfig />} />
              <Route path="login-audit" element={<LoginAudit />} />
              <Route path="sso-config" element={<SsoConfig />} />
              <Route
                path="*"
                element={<Navigate replace to="/settings/general/org-levels" />}
              />
            </Route>
            <Route path="organization" element={<Organization />} />
            <Route path="unit/:id" element={<SingleUnit />} />
            <Route path="Positions" element={<Positions />} />
            <Route path="users" element={<Users />} />
            <Route path="perf-cycles" element={<PerformanceCycles />} />
            <Route path="approvals" element={<Approvals />} />
            <Route path="target-approvals" element={<TargetApprovals />} />
            <Route path="plan-approvals" element={<PlanApprovals />} />
            <Route path="chatbot" element={<ChatbotSettings />} />
            <Route
              path="*"
              element={<Navigate replace to="/settings/user-profile" />}
            />
          </Route>
        </Route>
      </Route>
    </Routes>
  ) : (
    <MainLoader />
  );
}

export default Main;
